/*
 * Shared form styling for per-page settings panels (HomePage, WeatherPage, …).
 * Consumes the same liquid-glass shell as PlaceholderPanel so the panels read
 * as the same surface; only the inner controls differ per page.
 *
 * Vertical budget: panels render inside the 1920x1080 KioskStage with the
 * page heading + tab strip eating the top ~280px, so the panel content area
 * is ≈760px tall. Lists must paginate (4–5 items per page) rather than grow,
 * and forms with many fields use the .twoCol grid below.
 */

.panel {
  padding: 18px 22px;
}

.header {
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.headerText {
  flex: 1;
  min-width: 0;
}

.resetButton {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(176, 58, 46, 0.3);
  background: transparent;
  color: rgba(176, 58, 46, 0.85);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.resetButton:hover:not(:disabled) {
  background: rgba(176, 58, 46, 0.08);
  color: #b03a2e;
  border-color: rgba(176, 58, 46, 0.55);
}

.resetButton:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Right-side cluster in a panel header: "Reset settings" + "Remove page". */
.actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* "Remove page" — same small destructive family as Reset, slightly
 * stronger (solid red on hover) since it drops the whole page. */
.removeButton {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(176, 58, 46, 0.5);
  background: rgba(176, 58, 46, 0.06);
  color: rgba(176, 58, 46, 0.95);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.removeButton:hover {
  background: #b03a2e;
  color: #fff;
  border-color: #b03a2e;
}

/* Enable/disable pill for the permanent classroom pages (YouTube, Links).
 * Green-ish when enabled (shown in class), muted grey when disabled. */
.pageEnableButton {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(120, 130, 140, 0.45);
  background: rgba(120, 130, 140, 0.1);
  color: rgba(90, 100, 110, 0.9);
  cursor: pointer;
  flex-shrink: 0;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.pageEnableButtonOn {
  border-color: rgba(38, 122, 78, 0.5);
  background: rgba(38, 122, 78, 0.12);
  color: rgba(28, 100, 60, 0.95);
}

.heading {
  margin: 0 0 2px;
  font-family: var(--token-font-display);
  font-size: 20px;
  color: var(--token-text-primary);
}

.description {
  margin: 0;
  color: rgba(28, 38, 32, 0.78);
  font-size: 13px;
  line-height: 1.4;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: var(--token-font-body);
}

/* Two-column grid for panels with side-by-side sections (Daily/Weekly,
 * SchoolCal/K-2, Events/Birthdays). Collapses to 1-col under 720px wide. */
.twoCol {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 22px;
  align-items: start;
}

@media (max-width: 720px) {
  .twoCol {
    grid-template-columns: 1fr;
  }
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.label {
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  color: rgba(28, 38, 32, 0.85);
  text-transform: uppercase;
}

.hint {
  font-size: 11px;
  color: rgba(28, 38, 32, 0.62);
  line-height: 1.35;
}

.input,
.textarea {
  font-family: var(--token-font-body);
  font-size: 14px;
  color: var(--token-text-primary);
  padding: 7px 12px;
  border-radius: 10px;
  border: 1px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.62);
  transition:
    border-color 120ms ease,
    background 120ms ease,
    box-shadow 120ms ease;
}

.input:focus,
.textarea:focus {
  outline: none;
  border-color: var(--token-accent);
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--token-accent) 25%, transparent);
}

.input:disabled,
.textarea:disabled {
  opacity: 0.6;
  cursor: progress;
}

.textarea {
  resize: vertical;
  min-height: 56px;
  line-height: 1.4;
}

.textareaCompact {
  min-height: 38px;
}

.checkboxRow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  color: var(--token-text-primary);
  cursor: pointer;
  user-select: none;
}

.checkboxRow input[type='checkbox'] {
  width: 18px;
  height: 18px;
  accent-color: var(--token-accent);
  cursor: pointer;
}

.section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 4px;
}

.sectionTitle {
  margin: 0;
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: rgba(28, 38, 32, 0.85);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* Scroll modifier — caps the list height so adding many items (e.g.
 * 10+ class events) doesn't push everything else off-screen. Adds
 * vertical scroll with a small inset shadow at the bottom edge as
 * a visual hint that there's more below. */
.listScroll {
  max-height: clamp(200px, 30vh, 360px);
  overflow-y: auto;
  /* A thin top + bottom inset shadow tells the user the list is
   * scrollable without needing a permanent scrollbar. */
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 8px,
    #000 calc(100% - 8px),
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0,
    #000 8px,
    #000 calc(100% - 8px),
    transparent 100%
  );
  /* Reserve gutter so the scrollbar doesn't reflow content. */
  scrollbar-gutter: stable;
}

.listItem {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.55);
  border: 1px solid rgba(28, 38, 32, 0.1);
  font-size: 13px;
}

/* Pager (< / > arrows) — used to step through long lists rather than
 * letting them grow past the panel height. */
.pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-family: var(--token-font-ui);
  font-size: 12px;
  color: rgba(28, 38, 32, 0.7);
  letter-spacing: 0.2px;
}

.pagerButton {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.7);
  color: var(--token-text-primary);
  font-size: 14px;
  cursor: pointer;
  padding: 0;
}

.pagerButton:hover:not(:disabled) {
  background: #ffffff;
}

.pagerButton:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.listItemMain {
  flex: 1;
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.listItemTitle {
  font-size: 14px;
  color: var(--token-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.listItemMeta {
  font-size: 11px;
  color: rgba(28, 38, 32, 0.6);
  flex-shrink: 0;
}

.dayPicker {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.dayChip {
  font-family: var(--token-font-ui);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.4px;
  padding: 3px 8px;
  border-radius: 999px;
  border: 1px solid rgba(28, 38, 32, 0.18);
  background: rgba(255, 255, 255, 0.6);
  color: rgba(28, 38, 32, 0.7);
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
}

.dayChip:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.9);
}

.dayChipActive {
  background: var(--token-accent);
  border-color: var(--token-accent);
  color: #fff;
}

.dayChipActive:hover:not(:disabled) {
  background: var(--token-accent);
}

.button {
  font-family: var(--token-font-ui);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgba(28, 38, 32, 0.2);
  background: rgba(255, 255, 255, 0.85);
  color: var(--token-text-primary);
  cursor: pointer;
  transition:
    background 120ms ease,
    transform 120ms ease;
}

.button:hover:not(:disabled) {
  background: #ffffff;
}

.button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.buttonPrimary {
  background: var(--token-accent);
  border-color: var(--token-accent);
  color: #fff;
}

.buttonPrimary:hover:not(:disabled) {
  background: color-mix(in srgb, var(--token-accent) 85%, #000);
}

.buttonGhost {
  background: transparent;
  border-color: transparent;
  color: rgba(28, 38, 32, 0.6);
  padding: 6px 10px;
}

.buttonGhost:hover:not(:disabled) {
  color: var(--token-text-primary);
  background: rgba(28, 38, 32, 0.08);
}

.statusLine {
  font-size: 12px;
  color: rgba(28, 38, 32, 0.55);
  font-family: var(--token-font-ui);
  letter-spacing: 0.2px;
}

.statusError {
  color: #b03a2e;
}

.empty {
  font-size: 14px;
  color: rgba(28, 38, 32, 0.55);
  font-style: italic;
  padding: 8px 0;
}

.formRow {
  display: flex;
  gap: 10px;
  align-items: flex-end;
}

.formRow .field {
  flex: 1;
}

/* "Open …" verify link beneath the School calendar fields. Reads as
 * a confirmation that the URL is saved + clickable, without doubling
 * the panel's vertical footprint. */
.calLink {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--token-font-ui);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid color-mix(in srgb, var(--token-accent) 55%, transparent);
  background: color-mix(in srgb, var(--token-accent) 12%, transparent);
  color: rgba(140, 78, 30, 0.95);
  text-decoration: none;
  transition: background 120ms ease;
}

.calLink:hover {
  background: color-mix(in srgb, var(--token-accent) 22%, transparent);
}
